import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { countOrZero, nullableNumber, nullableString } from '../utils/wire-value-utils';
import { DroneOwnership } from './drone-ownership.model';
import { DroneReportScope } from './drone-report-scope.model';
import { DroneReportStatus } from './drone-report-status.model';

/**
 * A drone report (PDF), built from the hub's drone-report JSON (`user-drone-reports-controller.ts`).
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays
 * `null`). Only the hub-controlled `status`, `scope` and `generationProgress` get defaults.
 */
export class DroneReport extends Base {
  // Writable field
  name?: string | null;

  // Set on create
  organizationId?: string | null;
  droneId?: string | null;
  droneSource?: DroneOwnership | null;
  /** Defaults to 'full_history' only when the hub omits it. */
  scope: DroneReportScope;
  sprayRecordId?: string | null;
  startDate?: string | null;
  endDate?: string | null;

  // Read-only fields
  /** Hub-controlled lifecycle; defaults to 'draft' (the hub's default) only when omitted. */
  status: DroneReportStatus;
  generationProgress: number;
  recordCount?: number | null;
  fileSize?: string | null;
  reportUrl?: string | null;
  /** Pre-signed S3 URL, only on `GET /drone-reports/:id`. */
  downloadUrl?: string | null;
  submittedAt?: string | null;
  submittedTo?: string | null;
  errorMessage?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.name = nullableString(source['name']);
    this.organizationId = nullableString(source['organizationId']);
    this.droneId = nullableString(source['droneId']);
    this.droneSource = nullableString(source['droneSource']) as DroneOwnership | null | undefined;
    this.scope = (nullableString(source['scope']) ?? 'full_history') as DroneReportScope;
    this.sprayRecordId = nullableString(source['sprayRecordId']);
    this.startDate = nullableString(source['startDate']);
    this.endDate = nullableString(source['endDate']);
    this.status = (nullableString(source['status']) ?? 'draft') as DroneReportStatus;
    this.generationProgress = countOrZero(source['generationProgress']);
    this.recordCount = nullableNumber(source['recordCount']);
    this.fileSize = nullableString(source['fileSize']);
    this.reportUrl = nullableString(source['reportUrl']);
    this.downloadUrl = nullableString(source['downloadUrl']);
    this.submittedAt = nullableString(source['submittedAt']);
    this.submittedTo = nullableString(source['submittedTo']);
    this.errorMessage = nullableString(source['errorMessage']);
  }
}
